import type { ReactNode } from 'react';
import {
  FindingTimelineSeverityChangeEventTypeSchema,
  type FindingTimelineDto,
} from '@terra/shared-contracts-findings';
import { sort } from 'radash';
import type { TimelineStepData } from '../timeline';

const JUSTIFICATION_PREVIEW_MAX_LENGTH = 120;

type SeverityChangeTimelineEvent =
  FindingTimelineDto['severityChangeEvents'][number];

type SeverityChangeTimelineMessageKey =
  | 'severityChangeRequested'
  | 'severityChanged'
  | 'severityChangeRejected'
  | 'severityChangeFromTo'
  | 'severityChangeRequestedBy'
  | 'severityChangeApprovedBy'
  | 'severityChangeRejectedBy'
  | 'severityChangeRejectedNote';

type TranslateSeverityChangeTimeline = (
  key: SeverityChangeTimelineMessageKey,
  values?: Record<string, string>,
) => string;

const truncateNote = ({ note }: { note: string }): string => {
  const trimmed = note.trim();

  if (trimmed.length <= JUSTIFICATION_PREVIEW_MAX_LENGTH) {
    return trimmed;
  }

  return `${trimmed.slice(0, JUSTIFICATION_PREVIEW_MAX_LENGTH).trimEnd()}…`;
};

const getSeverityChangeStepCopy = ({
  event,
  t,
}: {
  event: SeverityChangeTimelineEvent;
  t: TranslateSeverityChangeTimeline;
}): {
  title: string;
  detailTitle: string;
  description?: string;
} => {
  const actorName = event.actorName?.trim() || '';
  const fromTo = t('severityChangeFromTo', {
    fromSeverity: event.fromSeverity,
    toSeverity: event.toSeverity,
  });

  switch (event.type) {
    case FindingTimelineSeverityChangeEventTypeSchema.enum.Requested:
      return {
        title: t('severityChangeRequested'),
        detailTitle: t('severityChangeRequestedBy', {
          actor: actorName,
          fromTo,
        }),
        description: event.note
          ? truncateNote({ note: event.note })
          : undefined,
      };
    case FindingTimelineSeverityChangeEventTypeSchema.enum.Changed:
      return {
        title: t('severityChanged'),
        detailTitle: t('severityChangeApprovedBy', {
          actor: actorName,
          fromTo,
        }),
      };
    case FindingTimelineSeverityChangeEventTypeSchema.enum.Rejected:
      return {
        title: t('severityChangeRejected'),
        detailTitle: t('severityChangeRejectedBy', {
          actor: actorName,
        }),
        description: event.note
          ? t('severityChangeRejectedNote', {
              note: truncateNote({ note: event.note }),
            })
          : undefined,
      };
  }
};

export const buildSeverityChangeTimelineSteps = ({
  events,
  icon,
  t,
}: {
  events: FindingTimelineDto['severityChangeEvents'];
  icon: ReactNode;
  t: TranslateSeverityChangeTimeline;
}): TimelineStepData[] =>
  sort(events, (event) => new Date(event.createdAt).getTime()).map((event) => {
    const copy = getSeverityChangeStepCopy({ event, t });

    return {
      id: event.id,
      title: copy.title,
      icon,
      tone: 'accent' as const,
      details: [
        {
          timestamp: event.createdAt,
          title: copy.detailTitle,
          description: copy.description,
          descriptionLineClamp: 3 as const,
        },
      ],
    };
  });
